import AppDialog from '../../../../../components/common/AppDialog';
import {SVG} from '../../../../../constants/assetLists';
import {useEffect, useMemo, useReducer, useState} from 'react';
import AppButton from '../../../../../components/common/AppButton';
import AppTextInput from '../../../../../components/common/AppTextInput';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import ApprovalChecklist from './components/ApprovalChecklist';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {
  handleCreateChecklistCommentService,
  handleGetChecklistService,
  handleUpdateChecklistService,
} from '../../../../../services/operations/checklist';
import {getCorrectPath, hasPermission} from '../../../../../utils';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import AppTooltip from '../../../../../components/common/AppTooltip';
import {uploadFiles} from '../../../../../utils/uploadFiles';
import AddEditCheckList from '../AddEditCheckList';
import { ICONS } from '../../../../users/data';

type Props = {
  open: boolean;
  onClose: () => void;
  checklistId: string;
};

type GenericObject = Record<string, any>;

const INITIAL_STATE = {
  selectedIndex: null,
  selectedSection: false,
  addedText: '',
  enableCommentField: false,
};

const reducerFunc = (state: any = INITIAL_STATE, action: any) => {
  const {payload} = action;
  switch (action.type) {
    case 'OPEN_CLOSE_SECTION':
      return {
        addedText: '',
        enableCommentField: false,
        selectedIndex: payload?.idx,
        selectedSection: payload?.option,
      };
    case 'ADD_COMMENT':
      return {...state, addedText: payload};
    case 'EDIT_COMMENT':
      return {...state, addedText: payload, enableCommentField: true};
    case 'ENABLE_DISABLE_COMMENT_FIELD':
      return {
        ...state,
        addedText: '',
        enableCommentField: !state.enableCommentField,
      };
    case 'RESET':
      return INITIAL_STATE;
    default:
      return state;
  }
};

function ChecklistApproval({open, onClose, checklistId}: Props) {
  const {showSnackbar} = useSnackbar();

  const [state, dispatch] = useReducer(reducerFunc, INITIAL_STATE);

  const {selectedShift} = useOperationsContext();

  const [checklist, setChecklist] = useState<GenericObject | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditChecklist, setShowEditChecklist] = useState(false);

  const queryClient = useQueryClient();

  const {data: checklistData} = useQuery({
    queryKey: [QUERY_KEYS.GET_CHECKLIST, checklistId],
    queryFn: () => handleGetChecklistService(checklistId),
    enabled: !!open && !!checklistId,
    staleTime: 0,
    refetchInterval: open && checklistId ? 60000 : false,
  });

  useEffect(() => {
    if (open && checklistData) {
      handleChecklistPopulate(checklistData);
    }
  }, [checklistData, open]);

  const handleChecklistPopulate = (list: Record<string, any>) => {
    const data = {
      id: list?._id,
      departments: list?.departments,
      title: list?.title,
      description: list?.description,
      showInBrief: list?.showInBrief,
      approvalRequired: list?.approvalRequired,
      enableComments: list?.commentsEnabled,
      list: list?.list
        ?.filter((item: any) => item?.activeStatus === 'active')
        ?.map((item: any) => ({
          id: item?._id,
          text: item?.details,
          isApproved: item?.progressStatus === 'completed',
          checked: item?.activeStatus === 'active',
          files: [...item?.attachments, ...item?.images],
          comments: item?.comments?.map((c: any) => ({
            id: c?._id,
            contentType: c?.contentType,
            comment: c?.text,
            commentBy: {
              firstName: c?.senderId?.firstName,
              lastName: c?.senderId?.lastName,
            },
            updatedAt: c?.createdAt,
          })),
          addedComments: [],
          addedFiles: [],
        })),
    };

    setChecklist(data);
  };

  const toggleChecklistItem = (index: number) => {
    setChecklist(prev => ({
      ...prev,
      list: prev?.list.map((item: GenericObject, idx: number) =>
        idx === index ? {...item, isApproved: !item.isApproved} : item,
      ),
    }));
  };

  const handleAllApprovedClick = () => {
    setChecklist(prev => ({
      ...prev,
      list: prev?.list.map((item: GenericObject) => ({
        ...item,
        isApproved: true,
      })),
    }));
  };

  const handleModalClose = () => {
    dispatch({type: 'RESET'});
    setChecklist(null);
    onClose();
  };

  const onSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = await Promise.all(
        (checklist?.list ?? []).map(async (item: any) => {
          const imagesFiles: Array<{
            type?: string;
            url: string;
            addedBy?: string;
          }> = [];
          const attachmentsFiles: Array<{
            type?: string;
            url: string;
            addedBy?: string;
          }> = [];
          const checkComments: any[] = [];

          const oldItem = checklistData?.list?.find(
            (li: any) => li?._id === item?.id,
          );
          const oldComments = (oldItem?.comments ?? [])?.map((c: any) => ({
            ...c,
            senderId: c?.senderId?._id,
          }));
          const updatedOldComments = (oldItem?.comments ?? [])
            ?.filter((c: any) =>
              item?.comments?.some((nc: any) => nc?.id === c?._id),
            )
            ?.map((c: any) => ({
              ...c,
              senderId: c?.senderId?._id,
            }));

          const oldImages = oldItem?.images ?? [];
          const oldAttachments = oldItem?.attachments ?? [];

          const userId = localStorage.getItem('userId') ?? undefined;

          // upload comments
          for (const c of item?.addedComments ?? []) {
            const res = await handleCreateChecklistCommentService(checklistId, {
              itemId: item?.id,
              text: c.comment,
            });
            if (res?.success) {
              const newComments =
                res?.data?.list
                  ?.find((row: any) => row?._id === item?.id)
                  ?.comments?.filter((nc: any) =>
                    oldComments?.length > 0
                      ? !oldComments.some((oc: any) => oc._id === nc._id)
                      : true,
                  ) ?? [];

              checkComments.push(...newComments);
            }
          }

          // upload files
          for (const file of item?.addedFiles ?? []) {
            if (file?.type?.includes('image')) {
              const res = await uploadFiles([file]);
              imagesFiles.push({
                type: res?.[0]?.type,
                url: res?.[0]?.url,
                addedBy: userId,
              });
            } else {
              const res = await uploadFiles([file]);
              attachmentsFiles.push({
                type: res?.[0]?.type,
                url: res?.[0]?.url,
                addedBy: userId,
              });
            }
          }

          return {
            details: item?.text ?? '',
            progressStatus: item?.isApproved ? 'completed' : 'idle',
            activeStatus: item?.checked ? 'active' : 'inactive',
            comments: [...updatedOldComments, ...checkComments],
            images: [...oldImages, ...imagesFiles],
            attachments: [...oldAttachments, ...attachmentsFiles],
          };
        }),
      );

      await handleUpdateChecklist({
        id: checklistId,
        payload: {list: payload},
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors?.join('\n') ||
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong';
      showSnackbar({message: errorMessage, type: 'error'});
    } finally {
      setIsSubmitting(false);
    }
  };

  const {mutateAsync: handleUpdateChecklist} = useMutation({
    mutationFn: (data: Record<string, any>) =>
      handleUpdateChecklistService(data),
    onSuccess: data => {
      setIsSubmitting(false);

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS_SHIFT_ID, selectedShift],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ALL_CHECKLISTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GROUPED_CHECKLISTS],
      });

      showSnackbar({
        message: data?.message ?? 'הרשימה עודכנה בהצלחה',
        type: 'success',
      });
      handleModalClose();
    },
  });

  const handleOpenViewModal = () => {
    setShowEditChecklist(true);
    onClose();
  };

  const checklistHeaderText = useMemo(() => {
    const list = checklist?.list || [];

    if (list?.length === 0) return '0 ברשימה';

    const active = list?.filter((item: GenericObject) => item.checked)?.length;
    const inactive = list?.length - active;

    const activeText = active === 1 ? 'פעיל' : 'פעילים';
    const inactiveText = inactive === 1 ? 'כבוי' : 'כבויים';

    if (list?.length === 1) {
      return active === 1 ? `1 ${activeText}` : `1 ${inactiveText}`;
    }

    if (active === 0) {
      return `${inactive} ${inactiveText}`;
    }

    if (inactive === 0) {
      return `${active} ${activeText}`;
    }

    // Mixed
    return `${active} ${activeText} | ${inactive} ${inactiveText}`;
  }, [checklist]);
  const allApproved = useMemo(() => {
    return checklist?.list?.every((item: GenericObject) => item.isApproved);
  }, [checklist]);
  
  return (
    <>
      <AppDialog
        showModel={open}
        onClose={handleModalClose}
        titleField={
          <AppTooltip
            title={checklist?.title?.length > 70 ? checklist?.title : ''}>
            <p className="m-0 text-[28px] text-black">
              {checklist?.title
                ? checklist?.title?.length > 70
                  ? checklist?.title?.slice(0, 70) + '...'
                  : checklist?.title
                : 'לאשר רשימת בדיקה'}
            </p>
          </AppTooltip>
        }
        showHeaderLine={false}
        badge={
          checklist?.departments?.length > 0 && (
            <div className="flex flex-row-reverse">
              {hasPermission('manage_checklists') && (
                <AppButton
                  onClick={handleOpenViewModal}
                  variant="custom"
                  className="px-0 py-0 mx-4">
                  <img src={SVG.editPenCircle} />
                </AppButton>
              )}

              <div className="px-4 py-2 rounded-[16px] bg-pastelPink flex justify-center items-center">
                <div className=" flex items-center gap-2">
                  <img
                    src={ checklist?.departments[0]?._id === 'all'
                      ? SVG.saveAllIcon
                      : checklist?.departments[0]?.icon?.startsWith('icon')
                        ? ICONS.find(icon => icon.id === checklist?.departments[0]?.icon)?.icon
                        : getCorrectPath(checklist?.departments[0]?.icon, 'image')}
                    alt="icon"
                    className="w-[24px] h-[24px] object-contain"
                  />
                  <p className="m-0 text-2xl text-black">
                    {checklist?.departments[0]?.name}
                  </p>
                </div>
              </div>
            </div>
          )
        }
        width={924}
        fullHeight>
        <div className="h-full flex flex-col gap-2 pb-3">
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-[32px]">
            <div className="flex flex-col gap-4">
              <AppTextInput
                type="text"
                defaultValue={checklist?.description}
                disabled
                className="bg-white border-none"
                startIcon={
                  <img
                    src={SVG.descriptionDarkIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                }
              />

              <div className="flex pb-2 border-b-2 border-b-tealBlue">
                <p className="m-0 text-[16px] font-semibold text-black">
                  {checklistHeaderText}
                </p>
              </div>
            </div>

            {checklist?.list?.map(
              (item: Record<string, any>, index: number) => (
                <ApprovalChecklist
                  key={item?.id ?? index}
                  index={index}
                  item={item}
                  state={state}
                  dispatch={dispatch}
                  setChecklist={setChecklist}
                  toggleChecklistItem={toggleChecklistItem}
                  enableComments={checklist?.enableComments}
                />
              ),
            )}
          </div>

          <div className="flex justify-between px-[32px]">
            <div className="mt-2">
              <AppButton
                variant="custom"
                iconStart={
                  <img
                    src={
                      allApproved
                        ? SVG.completeCheckboxWhiteIcon
                        : SVG.regularCheckboxIcon
                    }
                    alt="icon"
                    className="w-[36px] h-[36px] object-contain"
                  />
                }
                className={`gap-3 text-2xl font-semibold px-5 transition-all duration-200
            ${
              allApproved
                ? 'text-white bg-green'
                : 'border-[1.5px] border-gray rounded-[150px] text-black hover:bg-mintGreen'
            }`}
                onClick={handleAllApprovedClick}>
                הכל צ’ק
              </AppButton>
            </div>
            <div className="flex items-center gap-2">
              <AppButton onClick={handleModalClose}>ביטול</AppButton>

              <AppButton
                variant="primary"
                isLoading={isSubmitting}
                disabled={checklist?.list.length == 0}
                onClick={onSubmit}>
                שמירה
              </AppButton>
            </div>
          </div>
        </div>
      </AppDialog>

      <AddEditCheckList
        checklistId={checklist?.id}
        open={showEditChecklist}
        onClose={() => setShowEditChecklist(false)}
      />
    </>
  );
}

export default ChecklistApproval;
